<template>
  <div class="flex flex-col min-h-0">

    <!-- Filter bar -->
    <div class="sticky z-30 px-4 py-3" style="top:92px; background:rgba(11,15,17,0.92); backdrop-filter:blur(8px); border-bottom:1px solid rgba(255,255,255,0.05);">
      <SearchField v-model="filter" :debounce="0" :placeholder="$t('wiki.filterPlaceholder')" />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="px-4 pt-4 space-y-3">
      <div v-for="i in 5" :key="i" class="h-16 rounded-xl bg-white/5 animate-pulse" />
    </div>

    <!-- Error -->
    <div v-else-if="loadError" class="flex flex-col items-center justify-center py-20 text-center px-8">
      <p class="text-red-400 font-medium">{{ loadError }}</p>
      <button class="mt-4 text-sm text-blue-400 underline" @click="load">{{ $t('common.retry') }}</button>
    </div>

    <!-- Empty -->
    <div v-else-if="!filtered.length" class="flex flex-col items-center justify-center py-20 text-center px-8">
      <div class="text-5xl mb-4">📄</div>
      <p class="text-gray-300 font-medium">{{ filter ? $t('wiki.noMatchingPages') : $t('wiki.noPagesYet') }}</p>
      <p class="text-gray-500 text-sm mt-1">{{ filter ? $t('wiki.tryDifferentSearch') : $t('wiki.createFirst') }}</p>
    </div>

    <!-- Page list -->
    <ul v-else class="px-4 py-3 space-y-2">
      <li
        v-for="page in filtered"
        :key="page.id"
        @click="router.push(`/wiki/${page.id}`)"
        class="flex items-center gap-3 p-3 rounded-xl cursor-pointer active:scale-[0.98] transition-transform"
        style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);"
      >
        <div class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style="background:#172038; border:1px solid rgba(96,165,250,0.2);">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-white truncate">{{ page.name }}</div>
          <div class="text-xs text-gray-500 mt-0.5">{{ $t('wiki.updatedAt', { date: formatDate(page.updated_at) }) }}</div>
        </div>
        <svg class="shrink-0 text-gray-600" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>
      </li>
    </ul>

    <!-- FAB: new wiki page -->
    <button
      class="fixed bottom-24 right-4 z-40 flex items-center gap-2 px-4 py-3 rounded-full font-semibold text-sm shadow-lg transition-all active:scale-95"
      style="background:#2563eb; color:#fff;"
      @click="router.push('/wiki/new')"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
      {{ $t('wiki.newPage') }}
    </button>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getWikiPages } from '@/api/api.js'
import SearchField from '@/components/search/SearchField.vue'

const router = useRouter()
const { t } = useI18n()

const pages = ref([])
const filter = ref('')
const loading = ref(false)
const loadError = ref(null)

const filtered = computed(() => {
  const q = filter.value.trim().toLowerCase()
  if (!q) return pages.value
  return pages.value.filter(p => p.name.toLowerCase().includes(q))
})

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const res = await getWikiPages()
    pages.value = res.results ?? res ?? []
  } catch (e) {
    loadError.value = e?.data?.detail ?? e?.message ?? t('wiki.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>
